import React from 'react';
import {
  Document,
  Page,
  Text,
  View,
  Image,
  StyleSheet,
} from '@react-pdf/renderer';
import { formatCNPJ } from '@/utils/formatters';

interface EquipmentItem {
  id: string;
  description: string | null;
  item_type: string;
  quantity: number;
  unit_price: number;
  total_price: number | null;
  position: number;
}

interface Equipment {
  id: string;
  equipment_name: string;
  brand: string | null;
  model: string | null;
  serial_number: string | null;
  reported_issue: string | null;
  diagnosis: string | null;
  accessories: string | null;
  position: number;
  service_order_items: EquipmentItem[];
}

interface ServiceOrderForPDF {
  service_order_number: string;
  status: string;
  responsible_name: string | null;
  collected_by: string | null;
  notes: string | null;
  internal_notes: string | null;
  expected_delivery_date: string | null;
  actual_delivery_date: string | null;
  start_date: string | null;
  total_parts: number;
  total_services: number;
  total_value: number;
  customer_name?: string | null;
  customers?: {
    name?: string | null;
    document_number?: string | null;
    phone?: string | null;
    email?: string | null;
    address?: string | null;
    address_number?: string | null;
    neighborhood?: string | null;
    city?: string | null;
    state?: string | null;
    zip_code?: string | null;
  } | null;
  signature_data?: string | null;
  signature_customer_name?: string | null;
  signature_customer_document?: string | null;
  signature_date?: string | null;
  service_order_equipment: Equipment[];
}

interface ServiceModuleConfigForPDF {
  equipment_label?: string;
  order_label?: string;
}

interface CompanyForPDF {
  name: string;
  razao_social?: string;
  cnpj?: string;
  endereco?: string;
  numero?: string;
  bairro?: string;
  cidade?: string;
  estado?: string;
  cep?: string;
  telefone?: string;
  email_corporativo?: string;
}

interface ReceiptPhoto {
  dataUrl: string;
  caption?: string | null;
}

interface Props {
  order: ServiceOrderForPDF;
  moduleConfig: ServiceModuleConfigForPDF | null;
  company: CompanyForPDF;
  logoBase64?: string;
  photos?: ReceiptPhoto[];
}

const s = StyleSheet.create({
  page:         { fontFamily: 'Helvetica', fontSize: 9, padding: 32, color: '#1a1a1a' },

  // ── Header espelhando o SaleDocument (mantendo 56/15/9) ───────────
  header:       { flexDirection: 'row', justifyContent: 'space-between',
                  alignItems: 'flex-start', marginBottom: 16,
                  borderBottom: '1pt solid #e2e8f0', paddingBottom: 12 },
  headerLeft:   { flexDirection: 'row', alignItems: 'flex-start', flex: 1 },
  headerRight:  { alignItems: 'flex-end', marginLeft: 12 },
  logo:         { width: 56, height: 56, objectFit: 'contain', marginRight: 12 },
  companyName:  { fontSize: 15, fontFamily: 'Helvetica-Bold', color: '#0f172a', marginBottom: 2 },
  companyInfo:  { fontSize: 9, color: '#64748b', lineHeight: 1.4 },
  badgeBox:     { backgroundColor: '#fffbeb', borderWidth: 1, borderColor: '#fcd34d',
                  borderRadius: 4, paddingHorizontal: 8, paddingVertical: 4,
                  alignItems: 'center', marginBottom: 4 },
  badgeLabel:   { fontSize: 7, fontFamily: 'Helvetica-Bold', color: '#b45309', letterSpacing: 0.5 },
  badgeNumber:  { fontSize: 16, fontFamily: 'Helvetica-Bold', color: '#0f172a' },
  metaLine:     { fontSize: 7, color: '#64748b', marginTop: 1 },
  metaBold:     { fontFamily: 'Helvetica-Bold' },

  section:      { marginTop: 12 },
  sectionTitle: { fontSize: 8, fontFamily: 'Helvetica-Bold', color: '#64748b',
                  textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 },

  // ── Bloco CLIENTE (espelha o SaleDocument) ────────────────────────
  clientSection: { borderWidth: 1, borderColor: '#e5e7eb', borderRadius: 4,
                   padding: 6, marginTop: 12, backgroundColor: '#f9fafb' },
  clientGrid:    { flexDirection: 'row', gap: 12 },
  clientCol:     { flex: 1 },
  clientName:    { fontSize: 8, fontFamily: 'Helvetica-Bold', color: '#1a1a1a', marginBottom: 1 },
  clientDetail:  { fontSize: 7, color: '#4b5563', lineHeight: 1.4 },
  row:          { flexDirection: 'row', marginBottom: 2 },
  label:        { width: '38%', color: '#64748b' },
  value:        { flex: 1 },
  equipCard:    { border: '1pt solid #e2e8f0', borderRadius: 3, padding: 8, marginTop: 6 },
  equipHeader:  { fontFamily: 'Helvetica-Bold', marginBottom: 4, fontSize: 9 },
  tableHeader:  { flexDirection: 'row', borderBottom: '0.5pt solid #cbd5e1',
                  paddingBottom: 2, marginBottom: 2 },
  tableRow:     { flexDirection: 'row', paddingVertical: 2,
                  borderBottom: '0.5pt solid #f1f5f9' },
  colDesc:      { flex: 1 },
  colQty:       { width: 36, textAlign: 'right', color: '#64748b' },
  colPrice:     { width: 64, textAlign: 'right' },
  totals:       { marginTop: 12, borderTop: '1pt solid #e2e8f0', paddingTop: 8 },
  totalRow:     { flexDirection: 'row', justifyContent: 'flex-end', marginBottom: 2 },
  totalLabel:   { width: 90, color: '#64748b' },
  totalValue:   { width: 72, textAlign: 'right' },
  totalFinal:   { fontFamily: 'Helvetica-Bold', fontSize: 10 },
  signatures:   { marginTop: 28, flexDirection: 'row', justifyContent: 'space-between' },
  sigLine:      { width: '44%', borderTop: '1pt solid #0f172a', paddingTop: 4,
                  color: '#64748b', fontSize: 8, textAlign: 'center' },

  // ── Bloco de assinatura digital (espelha o SaleDocument) ──────────
  signatureSection: { marginTop: 16, paddingTop: 8, borderTopWidth: 1, borderTopColor: '#e2e8f0' },
  signatureTitle:   { fontSize: 8, fontFamily: 'Helvetica-Bold', color: '#64748b',
                      textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 },
  signatureBox:     { borderWidth: 1, borderColor: '#d1d5db', borderRadius: 4,
                      borderStyle: 'dashed', minHeight: 80, marginTop: 4, marginBottom: 4,
                      alignItems: 'center', justifyContent: 'center', backgroundColor: '#f9fafb' },
  signatureImage:   { maxHeight: 75, maxWidth: 280, objectFit: 'contain' },
  signatureDivider: { height: 1, backgroundColor: '#d1d5db', marginTop: 4, marginBottom: 6 },
  signatureFields:  { flexDirection: 'row', justifyContent: 'space-between', marginTop: 4 },
  signatureField:   { flex: 1, marginRight: 8 },
  signatureLabel:   { fontSize: 7, color: '#6b7280', marginBottom: 2 },
  signatureValue:   { fontSize: 8, fontFamily: 'Helvetica-Bold', color: '#111111', minHeight: 10 },
  signatureUnderline: { height: 1, backgroundColor: '#374151', marginTop: 4 },

  // ── Fotos do equipamento (2 por linha) ────────────────────────────
  photosGrid:   { flexDirection: 'row', flexWrap: 'wrap', marginTop: 6 },
  photoCell:    { width: 240, marginRight: 12, marginBottom: 12 },
  photoImage:   { width: 240, height: 180, objectFit: 'contain',
                  borderWidth: 1, borderColor: '#e2e8f0', borderRadius: 3,
                  backgroundColor: '#f9fafb' },
  photoCaption: { fontSize: 7, color: '#64748b', marginTop: 3, textAlign: 'center' },

  footer:       { position: 'absolute', bottom: 20, left: 32, right: 32,
                  fontSize: 7, color: '#94a3b8', textAlign: 'center' },
});

function fmt(v: number) {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(v);
}

function fmtDate(d: string | null) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('pt-BR');
}

function fmtDateTime(d?: string | null) {
  if (!d) return '';
  const date = new Date(d);
  return `${date.toLocaleDateString('pt-BR')} ${date.toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  })}`;
}

export default function ServiceOrderReceiptDocument({
  order,
  moduleConfig,
  company,
  logoBase64,
  photos,
}: Props) {
  const equipLabel = moduleConfig?.equipment_label ?? 'Equipamento';
  const orderLabel = moduleConfig?.order_label ?? 'Ordem de Serviço';
  const sorted = [...(order.service_order_equipment ?? [])].sort(
    (a, b) => a.position - b.position,
  );

  // Endereço da EMPRESA por segmentos (nomes PT)
  const _coStreet = [company.endereco, company.numero].filter(Boolean).join(', ');
  const _coCity   = [company.cidade, company.estado].filter(Boolean).join('/');
  const companyAddress = [
    _coStreet,
    company.bairro,
    _coCity,
    company.cep && `CEP: ${company.cep}`,
  ].filter(Boolean).join(' - ');

  // Endereço do CLIENTE por segmentos (nomes EN) — mesmo builder do SaleDocument
  const cust = order.customers;
  const _custStreet = [cust?.address, cust?.address_number].filter(Boolean).join(', ');
  const _custCity   = [cust?.city, cust?.state].filter(Boolean).join('/');
  const clientAddress = [
    _custStreet,
    cust?.neighborhood,
    _custCity,
    cust?.zip_code && `CEP: ${cust.zip_code}`,
  ].filter(Boolean).join(' - ');

  const companyContact = [
    company.telefone          && `Tel: ${company.telefone}`,
    company.email_corporativo && `Email: ${company.email_corporativo}`,
  ].filter(Boolean).join('  |  ');

  return (
    <Document>
      <Page size="A4" style={s.page}>
        {/* Header empresa — espelha o SaleDocument (logo + nome + dados + selo) */}
        <View style={s.header}>
          {/* Esquerda: logo + empresa */}
          <View style={s.headerLeft}>
            {logoBase64 ? <Image src={logoBase64} style={s.logo} /> : null}
            <View style={{ flex: 1 }}>
              <Text style={s.companyName}>{company.name || company.razao_social}</Text>
              {company.cnpj ? (
                <Text style={s.companyInfo}>CNPJ: {formatCNPJ(company.cnpj)}</Text>
              ) : null}
              {companyAddress.length > 0 ? (
                <Text style={s.companyInfo}>{companyAddress}</Text>
              ) : null}
              {companyContact.length > 0 ? (
                <Text style={s.companyInfo}>{companyContact}</Text>
              ) : null}
            </View>
          </View>

          {/* Direita: selo COLETA + número + data */}
          <View style={s.headerRight}>
            <View style={s.badgeBox}>
              <Text style={s.badgeLabel}>COLETA</Text>
              <Text style={s.badgeNumber}>{order.service_order_number}</Text>
            </View>
            <Text style={s.metaLine}>
              <Text style={s.metaBold}>Data de Entrada: </Text>
              {fmtDate(order.start_date)}
            </Text>
          </View>
        </View>

        {/* Cliente — espelha o SaleDocument */}
        <View style={s.clientSection}>
          <Text style={s.sectionTitle}>Cliente</Text>
          <View style={s.clientGrid}>
            <View style={s.clientCol}>
              <Text style={s.clientName}>
                {order.customers?.name ?? order.customer_name ?? '—'}
              </Text>
              {cust?.document_number ? (
                <Text style={s.clientDetail}>
                  {cust.document_number.replace(/\D/g, '').length > 11 ? 'CNPJ' : 'CPF'}:{' '}
                  {cust.document_number}
                </Text>
              ) : null}
              {clientAddress.length > 0 ? (
                <Text style={s.clientDetail}>{clientAddress}</Text>
              ) : null}
            </View>
            <View style={s.clientCol}>
              {cust?.phone ? (
                <Text style={s.clientDetail}>Tel: {cust.phone}</Text>
              ) : null}
              {cust?.email ? (
                <Text style={s.clientDetail}>Email: {cust.email}</Text>
              ) : null}
            </View>
          </View>
        </View>

        {/* Dados da OS */}
        <View style={s.section}>
          <Text style={s.sectionTitle}>Dados da {orderLabel}</Text>
          <View style={s.row}>
            <Text style={s.label}>Data de Entrada:</Text>
            <Text style={s.value}>{fmtDate(order.start_date)}</Text>
          </View>
          {order.expected_delivery_date ? (
            <View style={s.row}>
              <Text style={s.label}>Entrega Prevista:</Text>
              <Text style={s.value}>{fmtDate(order.expected_delivery_date)}</Text>
            </View>
          ) : null}
          {order.collected_by ? (
            <View style={s.row}>
              <Text style={s.label}>Coletado por:</Text>
              <Text style={s.value}>{order.collected_by}</Text>
            </View>
          ) : null}
          <View style={s.row}>
            <Text style={s.label}>Responsável:</Text>
            <Text style={s.value}>{order.responsible_name ?? '—'}</Text>
          </View>
        </View>

        {/* Equipamentos */}
        {sorted.length > 0 ? (
          <View style={s.section}>
            <Text style={s.sectionTitle}>{equipLabel}(s)</Text>
            {sorted.map((eq, idx) => (
              <View key={eq.id} style={s.equipCard}>
                <Text style={s.equipHeader}>
                  {equipLabel} {idx + 1}: {eq.equipment_name}
                  {eq.brand ? ` — ${eq.brand}` : ''}
                  {eq.model ? ` ${eq.model}` : ''}
                </Text>

                {eq.serial_number ? (
                  <View style={s.row}>
                    <Text style={s.label}>Nº de Série:</Text>
                    <Text style={s.value}>{eq.serial_number}</Text>
                  </View>
                ) : null}
                {eq.reported_issue ? (
                  <View style={s.row}>
                    <Text style={s.label}>Problema Relatado:</Text>
                    <Text style={s.value}>{eq.reported_issue}</Text>
                  </View>
                ) : null}
                {eq.accessories ? (
                  <View style={s.row}>
                    <Text style={s.label}>Acessórios:</Text>
                    <Text style={s.value}>{eq.accessories}</Text>
                  </View>
                ) : null}

                {(eq.service_order_items ?? []).length > 0 ? (
                  <View style={{ marginTop: 6 }}>
                    <View style={s.tableHeader}>
                      <Text style={[s.colDesc, { fontFamily: 'Helvetica-Bold', fontSize: 8 }]}>
                        Descrição
                      </Text>
                      <Text style={[s.colQty, { fontFamily: 'Helvetica-Bold', fontSize: 8 }]}>
                        Qtd
                      </Text>
                      <Text style={[s.colPrice, { fontFamily: 'Helvetica-Bold', fontSize: 8 }]}>
                        Total
                      </Text>
                    </View>
                    {[...eq.service_order_items]
                      .sort((a, b) => a.position - b.position)
                      .map((item, iIdx) => (
                        <View key={`${eq.id}-${iIdx}`} style={s.tableRow}>
                          <Text style={s.colDesc}>{item.description ?? '—'}</Text>
                          <Text style={s.colQty}>{item.quantity}</Text>
                          <Text style={s.colPrice}>
                            {fmt(item.total_price ?? item.unit_price * item.quantity)}
                          </Text>
                        </View>
                      ))}
                  </View>
                ) : null}
              </View>
            ))}
          </View>
        ) : null}

        {/* Fotos do equipamento */}
        {photos?.length ? (
          <View style={s.section}>
            <Text style={s.sectionTitle}>Fotos do Equipamento</Text>
            <View style={s.photosGrid}>
              {photos.map((photo, idx) => (
                <View key={idx} style={s.photoCell} wrap={false}>
                  <Image src={photo.dataUrl} style={s.photoImage} />
                  {photo.caption ? (
                    <Text style={s.photoCaption}>{photo.caption}</Text>
                  ) : null}
                </View>
              ))}
            </View>
          </View>
        ) : null}

        {/* Observações */}
        {order.notes ? (
          <View style={s.section}>
            <Text style={s.sectionTitle}>Observações</Text>
            <Text>{order.notes}</Text>
          </View>
        ) : null}

        {/* Assinatura — espelha o bloco do SaleDocument */}
        <View style={s.signatureSection} wrap={false}>
          <Text style={s.signatureTitle}>Assinatura do Cliente</Text>

          <View style={s.signatureBox}>
            {order.signature_data ? (
              <Image src={order.signature_data} style={s.signatureImage} />
            ) : (
              <View style={{ height: 60 }} />
            )}
          </View>

          <View style={s.signatureDivider} />

          <View style={s.signatureFields}>
            <View style={s.signatureField}>
              <Text style={s.signatureLabel}>Nome do responsável:</Text>
              <Text style={s.signatureValue}>{order.signature_customer_name ?? ''}</Text>
              <View style={s.signatureUnderline} />
            </View>

            <View style={s.signatureField}>
              <Text style={s.signatureLabel}>CPF/CNPJ:</Text>
              <Text style={s.signatureValue}>{order.signature_customer_document ?? ''}</Text>
              <View style={s.signatureUnderline} />
            </View>

            {order.signature_date && (
              <View style={s.signatureField}>
                <Text style={s.signatureLabel}>Data:</Text>
                <Text style={s.signatureValue}>{fmtDateTime(order.signature_date)}</Text>
                <View style={s.signatureUnderline} />
              </View>
            )}
          </View>
        </View>

        <Text style={s.footer}>
          Documento gerado em{' '}
          {new Date().toLocaleDateString('pt-BR')} às{' '}
          {new Date().toLocaleTimeString('pt-BR', {
            hour: '2-digit',
            minute: '2-digit',
          })}
        </Text>
      </Page>
    </Document>
  );
}
